import {OfficialViewModel} from "../../../../../domain/full-game/models/official/official-view-model";
import {VenueViewModel} from "../../../models/venue-view-model";
import {TvNetworkViewModel} from "../../../../../domain/common/tv-network/models/tv-network-view-model";
import {BaseModuleState} from "../../../../../infrastructure/models/base-module-state";
import {computed, override} from "mobx";
import {GoogleMapsLocationParams} from "./google-map-location-params";
import {TvNetworkFactory} from "../../../../../domain/common/tv-network/domain-services/tv-network.factory";
import {OfficialDomainModel} from "../../../../../domain/full-game/models/official/official-domain-model";
import {CurrentOddsModuleConfig} from "../../current-odds-module/current-odds-module.config";
import {AppStore} from "../../../../../core/app/app-store";
import {LoggerUtils} from "../../../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../../../core/models/device.view-model";
import {TermsUtils} from "../../../../../infrastructure/utils/terms-utils";
import {contractsUtils} from "../../../../../infrastructure/utils/contracts-utils";
import {NavigationManager} from "../../../../../core/managers/navigation-manager";
import {BaseMobxState} from "../../../../../infrastructure/mobx/base-mobx-state";
import {GameInfoModuleConfig} from "../game-info-module.config";
import {FullGameDataSource} from "../../../../../domain/full-game/models/full-game-data-source";
import {MathUtils} from "../../../../../infrastructure/utils/math-utils";
import {GameInfoModuleItem} from "./game-info-module.item";
import {GAME_INFO_MODULE} from "../game-info-module.consts";
import type {GameInfoModuleTerms} from "../game-info-module.terms";
import {COMPONENT_STATES} from "../../../../../core/consts";

export class GameInfoModuleState extends BaseModuleState {
  dataSource: FullGameDataSource;
  tvNetworkFactory: TvNetworkFactory;
  terms: GameInfoModuleTerms;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, GameInfoModuleConfig, TvNetworkFactory, MathUtils];

  constructor(
    navigationManager: NavigationManager,
    appStore: AppStore,
    logger: LoggerUtils,
    datetimeUtils: DatetimeUtils,
    device: DeviceViewModel,
    termsUtils: TermsUtils,
    config: CurrentOddsModuleConfig,
    tvNetworkFactory: TvNetworkFactory,
    mathUtils: MathUtils
  ) {
    super(navigationManager, appStore, logger, datetimeUtils, device, termsUtils, config);

    contractsUtils.requiredType(tvNetworkFactory, TvNetworkFactory);

    this.mathUtils = mathUtils;
    this.tvNetworkFactory = tvNetworkFactory;
  }

  @override get isPopulated() {
    const isPopulated = this.componentState === COMPONENT_STATES.loading || this.items.length > 0;

    return isPopulated;
  }

  get items(): Array<GameInfoModuleItem> {
    const items: Array<GameInfoModuleItem> = [
      this.referee,
      this.stadium,
      this.tvNetworks
    ].removeNulls();

    return items;
  }

  @computed get stadium(): VenueViewModel {
    const venue: VenueViewModel = this.dataSource?.game.venue && new VenueViewModel(this.dataSource?.game.venue, this.mathUtils);
    let subtitle = null;

    if (venue?.formattedAttendance) {
      subtitle = `${this.terms.GAME_CENTER_ATTENDANCE}: ${venue?.formattedAttendance}`;
    } else if (venue?.formattedCapacity) {
      subtitle = `${this.terms.GC_CAPACITY}: ${venue?.formattedCapacity}`;
    }

    const locationLink = this.dataSource?.game.venue?.googlePlaceId ? new GoogleMapsLocationParams(this.dataSource?.game.venue.googlePlaceId) : null;

    return venue ? new GameInfoModuleItem(GAME_INFO_MODULE.TYPES.stadium, venue.name, subtitle, locationLink, this.terms.GAME_INFO_SEE_STADIUM) : null;
  }

  @computed get referee(): OfficialViewModel {
    const referee: OfficialDomainModel = this.dataSource?.game.officials?.first();
    const refereeVM: OfficialViewModel = referee && new OfficialViewModel(referee);

    return referee ? new GameInfoModuleItem(GAME_INFO_MODULE.TYPES.referee, refereeVM?.name, this.terms.GAME_CENTER_REFEREE) : null;
  }

  @computed get tvNetworks(): Array<TvNetworkViewModel> {
    const tvNetworks: Array<TvNetworkViewModel> = this.dataSource?.game.tvNetworks && this.dataSource?.game.tvNetworks.map((tv) => this.tvNetworkFactory.createVM(tv));
    const subtitle = tvNetworks?.map((tv) => tv.name).join(' / ');

    return tvNetworks?.length > 0 ? new GameInfoModuleItem(GAME_INFO_MODULE.TYPES.tvNetwork, this.terms.GAME_CENTER_TV_CHANNEL, subtitle) : null;
  }
}
